import React from 'react';

export type PeriodType = '7days' | '30days' | '1year' | 'custom';

export interface DateRange {
  startDate: Date;
  endDate: Date;
}

interface PeriodSelectorProps {
  selectedPeriod: PeriodType;
  onPeriodChange: (period: PeriodType, dateRange: DateRange) => void;
  currentRange: DateRange;
}

export const getDateRangeForPeriod = (period: PeriodType, customStart?: Date, customEnd?: Date): DateRange => {
  const now = new Date();
  const endDate = new Date(now);
  endDate.setHours(23, 59, 59, 999);

  let startDate: Date;

  switch (period) {
    case '7days':
      startDate = new Date(now);
      startDate.setDate(startDate.getDate() - 7);
      startDate.setHours(0, 0, 0, 0);
      break;
    case '30days':
      startDate = new Date(now);
      startDate.setDate(startDate.getDate() - 30);
      startDate.setHours(0, 0, 0, 0);
      break;
    case '1year':
      startDate = new Date(now);
      startDate.setFullYear(startDate.getFullYear() - 1);
      startDate.setHours(0, 0, 0, 0);
      break;
    case 'custom':
      startDate = customStart || new Date(now.setDate(now.getDate() - 30));
      startDate.setHours(0, 0, 0, 0);
      if (customEnd) {
        endDate.setTime(customEnd.getTime());
        endDate.setHours(23, 59, 59, 999);
      }
      break;
    default:
      startDate = new Date(now);
      startDate.setDate(startDate.getDate() - 30);
      startDate.setHours(0, 0, 0, 0);
  }

  return { startDate, endDate };
};

export const formatDateForDisplay = (date: Date): string => {
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }).format(date);
};

const formatDateForInput = (date: Date): string => {
  return date.toISOString().split('T')[0];
};

const inputClass = 'px-3 py-2 border border-cream-high rounded-xl bg-white text-ink text-sm focus:outline-none focus:ring-2 focus:ring-coral/30 focus:border-coral';

export const PeriodSelector: React.FC<PeriodSelectorProps> = ({
  selectedPeriod,
  onPeriodChange,
  currentRange
}) => {

  const handlePeriodSelectChange = (period: PeriodType) => {
    if (period === 'custom') {
      onPeriodChange('custom', currentRange);
    } else {
      const newRange = getDateRangeForPeriod(period);
      onPeriodChange(period, newRange);
    }
  };

  const handleDateInputChange = (type: 'start' | 'end', value: string) => {
    const newDate = new Date(value);
    if (isNaN(newDate.getTime())) return;

    const newRange = { ...currentRange };
    if (type === 'start') {
      newDate.setHours(0, 0, 0, 0);
      newRange.startDate = newDate;
    } else {
      newDate.setHours(23, 59, 59, 999);
      newRange.endDate = newDate;
    }

    onPeriodChange('custom', newRange);
  };

  return (
    <div className="flex flex-wrap items-center gap-3">
      <span className="text-sm font-medium text-ink-soft">Période :</span>

      <select
        value={selectedPeriod}
        onChange={(e) => handlePeriodSelectChange(e.target.value as PeriodType)}
        className={inputClass}
      >
        <option value="7days">7 derniers jours</option>
        <option value="30days">30 derniers jours</option>
        <option value="1year">1 an</option>
        <option value="custom">Période personnalisée</option>
      </select>

      {selectedPeriod === 'custom' && (
        <div className="flex items-center gap-4 ml-2">
          <div className="flex items-center gap-2">
            <label htmlFor="startDate" className="text-sm text-ink-soft whitespace-nowrap">
              Du :
            </label>
            <input
              id="startDate"
              type="date"
              value={formatDateForInput(currentRange.startDate)}
              max={formatDateForInput(currentRange.endDate)}
              onChange={(e) => handleDateInputChange('start', e.target.value)}
              className={inputClass}
            />
          </div>

          <div className="flex items-center gap-2">
            <label htmlFor="endDate" className="text-sm text-ink-soft whitespace-nowrap">
              Au :
            </label>
            <input
              id="endDate"
              type="date"
              value={formatDateForInput(currentRange.endDate)}
              min={formatDateForInput(currentRange.startDate)}
              max={formatDateForInput(new Date())}
              onChange={(e) => handleDateInputChange('end', e.target.value)}
              className={inputClass}
            />
          </div>
        </div>
      )}
    </div>
  );
};
